import i18n from '@/utils/i18n';
import React, { useRef } from 'react';
import {
  Animated,
  NativeScrollEvent,
  NativeSyntheticEvent,
  ScrollView,
  Text,
} from 'react-native';
import { BadgeLabelList } from '@/components/orders/badge-label-list';
import { createStyles } from '@/contexts/theme-context';
import { DSR_TOOLING_ROUTES, ORDER_INBOUNDS_ROUTES } from '@/navigators/routes';
import {
  DSRToolingStackParams,
  OrderInboundsStackParams,
} from '@/navigators/types';
import { FONTS, TYPOGRAPHY } from '@/styles';
import { ModalContainer } from '@/components/modal-container';
import { OrderableItemListItemImage } from '@/components/orders/orderable-item-list-item-image';
import { SalesRepDetails } from '@/components/sales-rep/sales-rep-details';
import { StackScreenProps } from '@react-navigation/stack';
import { useItemDisplayOptions } from '@/contexts/new-order-context';
import { usePriceEditing } from '@/hooks/use-price-editing';

type Props =
  | StackScreenProps<DSRToolingStackParams, DSR_TOOLING_ROUTES.Base>
  | StackScreenProps<
      OrderInboundsStackParams,
      ORDER_INBOUNDS_ROUTES.DSRTooling
    >;

export const DSRToolingScreen: React.FC<Props> = ({ route }) => {
  const {
    params: { itemMetadata, onSalesRepPriceUpdated, updateQuantity },
  } = route;
  const variantPackUUID = itemMetadata.variantPackUUID;
  const { hidePlaceholderImages } = useItemDisplayOptions();
  const { isPriceEditingEnabled } = usePriceEditing(variantPackUUID);
  const [styles] = useStyles();
  const scrollY = useRef(new Animated.Value(0)).current;

  const headerOpacity = scrollY.interpolate({
    extrapolate: 'clamp',
    inputRange: [50, 100],
    outputRange: [0, 1],
  });

  const headerTranslateY = scrollY.interpolate({
    extrapolate: 'clamp',
    inputRange: [50, 100],
    outputRange: [-10, 0],
  });

  const handleScroll = (event: NativeSyntheticEvent<NativeScrollEvent>) => {
    const yOffset = event.nativeEvent.contentOffset.y;
    scrollY.setValue(yOffset);
  };

  const altText = [itemMetadata.displayName, itemMetadata.externalID]
    .filter(Boolean)
    .join('-');

  return (
    <ModalContainer
      navigationHeaderProps={{
        title: (
          <Animated.View
            style={[
              styles.base,
              {
                opacity: headerOpacity,
                transform: [{ translateY: headerTranslateY }],
              },
            ]}
          >
            <OrderableItemListItemImage
              alt={altText}
              hidePlaceholderImages={hidePlaceholderImages}
              imageURLs={itemMetadata?.photoURLs ?? []}
              style={styles.imagePreview}
            />
            <Text
              numberOfLines={1}
              style={[TYPOGRAPHY.listTitle, FONTS.primarySemiBold]}
            >
              {itemMetadata?.displayName}
            </Text>
            <BadgeLabelList badges={itemMetadata.badges} />
          </Animated.View>
        ),
      }}
    >
      <ScrollView
        keyboardShouldPersistTaps="always"
        onScroll={handleScroll}
        scrollEventThrottle={16}
      >
        <Text style={[TYPOGRAPHY.h3, styles.title]}>
          {isPriceEditingEnabled
            ? i18n.t('edit_price')
            : i18n.t('view_details')}
        </Text>
        <SalesRepDetails
          itemMetadata={itemMetadata}
          onSalesRepPriceUpdated={onSalesRepPriceUpdated}
          updateQuantity={updateQuantity}
        />
      </ScrollView>
    </ModalContainer>
  );
};

const useStyles = createStyles((theme) => ({
  base: {
    alignItems: 'center',
    flexDirection: 'row',
    gap: 8,
  },
  imagePreview: {
    borderColor: theme.colors.imageBorder,
    borderRadius: 8,
    borderStyle: 'solid',
    borderWidth: 1,
    height: 32,
    width: 32,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    lineHeight: 32,
    marginHorizontal: 24,
  },
}));
